import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Sun, Moon, Menu, X, Newspaper } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function Header() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      nav(`/search?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setOpen(false);
    }
  };

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  return (
    <header className="border-b bg-[color:var(--surface)] sticky top-0 z-40 backdrop-blur" data-testid="site-header">
      {/* Top bar */}
      <div className="hidden md:block border-b" style={{ borderColor: "hsl(var(--border-hsl))" }}>
        <div className="container-editorial flex items-center justify-between py-2 text-xs">
          <span className="kicker text-[color:var(--text-muted)]" data-testid="header-date">{today}</span>
          <div className="flex items-center gap-4 text-[color:var(--text-muted)]">
            <a href="#" className="hover-underline">Twitter</a>
            <a href="#" className="hover-underline">Instagram</a>
            <a href="#" className="hover-underline">LinkedIn</a>
            <button onClick={toggle} className="ml-2 p-1" data-testid="theme-toggle" aria-label="Toggle theme">
              {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            </button>
          </div>
        </div>
      </div>

      {/* Masthead */}
      <div className="container-editorial flex items-center justify-between py-6 md:py-8">
        <div className="w-24 md:w-40 flex items-center gap-2">
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="menu-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
          <button onClick={() => setSearchOpen(!searchOpen)} className="hidden md:flex items-center gap-2 text-xs kicker hover-underline" data-testid="search-toggle">
            <Search size={14} /> Search
          </button>
        </div>

        <Link to="/" className="flex items-center gap-3 md:gap-4" data-testid="logo-link">
          <img
            src="/logo-badge.jpg"
            alt="ITTV News"
            className="h-12 md:h-16 w-auto rounded-md shadow-md"
            width="120"
            height="120"
            data-testid="site-logo-badge"
          />
          <div className="text-left">
            <div className="font-serif text-2xl md:text-4xl leading-none font-bold tracking-tight">ITTV News</div>
            <div className="kicker mt-1 text-[color:var(--text-muted)]">Independent Reporting · Est. 2026</div>
          </div>
        </Link>

        <div className="w-24 md:w-40 flex items-center justify-end gap-3">
          {user && user.role === "admin" ? (
            <>
              <Link to="/admin" className="hidden md:inline-block kicker hover-underline" data-testid="nav-admin">Admin</Link>
              <button onClick={logout} className="hidden md:inline-block kicker hover-underline" data-testid="nav-logout">Logout</button>
            </>
          ) : (
            <Link to="/login" className="hidden md:inline-block kicker hover-underline" data-testid="nav-login">Log in</Link>
          )}
          <button onClick={toggle} className="md:hidden p-1" data-testid="theme-toggle-mobile" aria-label="Toggle theme">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </div>

      {/* Nav bar */}
      <nav className="border-t border-b" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="primary-nav">
        <div className="container-editorial hidden md:flex items-center gap-8 py-3 overflow-x-auto">
          <NavLink to="/" end className={({ isActive }) => `kicker hover-underline ${isActive ? "text-[color:var(--accent)]" : ""}`} data-testid="nav-home">Home</NavLink>
          {cats.map((c) => (
            <NavLink
              key={c.slug}
              to={`/category/${c.slug}`}
              className={({ isActive }) => `kicker hover-underline whitespace-nowrap ${isActive ? "text-[color:var(--accent)]" : ""}`}
              data-testid={`nav-cat-${c.slug}`}
            >
              {c.name}
            </NavLink>
          ))}
          <NavLink to="/videos" className={({ isActive }) => `kicker hover-underline ${isActive ? "text-[color:var(--accent)]" : ""}`} data-testid="nav-videos">Video & Audio</NavLink>
          <NavLink to="/about" className={({ isActive }) => `kicker hover-underline ${isActive ? "text-[color:var(--accent)]" : ""}`} data-testid="nav-about">About</NavLink>
          <NavLink to="/subscribe" className={({ isActive }) => `kicker hover-underline ml-auto text-[color:var(--accent)] font-bold ${isActive ? "underline" : ""}`} data-testid="nav-subscribe">Subscribe · $5</NavLink>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden container-editorial py-4 flex flex-col gap-3" data-testid="mobile-menu">
            <form onSubmit={submitSearch} className="flex border" style={{ borderColor: "hsl(var(--border-hsl))" }}>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search stories" className="flex-1 bg-transparent px-3 py-2 outline-none text-sm" data-testid="mobile-search-input" />
              <button className="btn-accent" data-testid="mobile-search-btn">Go</button>
            </form>
            <NavLink to="/" end onClick={() => setOpen(false)} className="kicker">Home</NavLink>
            {cats.map((c) => (
              <NavLink key={c.slug} to={`/category/${c.slug}`} onClick={() => setOpen(false)} className="kicker">{c.name}</NavLink>
            ))}
            <NavLink to="/videos" onClick={() => setOpen(false)} className="kicker">Video & Audio</NavLink>
            <NavLink to="/about" onClick={() => setOpen(false)} className="kicker">About</NavLink>
            <NavLink to="/subscribe" onClick={() => setOpen(false)} className="kicker text-[color:var(--accent)] font-bold">Subscribe · $5</NavLink>
            {user && user.role === "admin" ? (
              <>
                <NavLink to="/admin" onClick={() => setOpen(false)} className="kicker">Admin</NavLink>
                <button onClick={() => { logout(); setOpen(false); }} className="kicker text-left">Logout</button>
              </>
            ) : (
              <NavLink to="/login" onClick={() => setOpen(false)} className="kicker">Log in</NavLink>
            )}
          </div>
        )}
      </nav>

      {/* Search bar (desktop) */}
      {searchOpen && (
        <div className="border-b" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="search-bar">
          <div className="container-editorial py-4">
            <form onSubmit={submitSearch} className="flex items-center gap-3">
              <Search size={20} className="text-[color:var(--text-muted)]" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search stories, authors, tags..."
                className="flex-1 bg-transparent outline-none font-serif text-2xl md:text-3xl"
                data-testid="search-input"
              />
              <button type="button" onClick={() => setSearchOpen(false)} className="text-[color:var(--text-muted)]"><X /></button>
            </form>
          </div>
        </div>
      )}
    </header>
  );
}
